import { Select } from '@zoopla-eng/zds/select';
import { fontGroups } from '@zoopla-eng/zds/themes';
import {
    dropdownWrapperStyle,
    filtersLabelStyle,
    filterWrapperStyle,
    leftWrapperStyle,
    priceFrequencyWrapperStyle,
    rightWrapperStyle
} from './Options.styles.css';

const priceRangeLabel = 'Price range';
const maxPriceLabel = 'Max price';
const minPriceLabel = 'Min price';
const priceFrequencyLabel = 'Price per';

const priceFrequencyOptions: { [key: PriceFrequency]: string } = {
    per_month: 'pcm',
    per_week: 'pcw'
};

const priceOptions = [
    '25',
    '50',
    '75',
    '100',
    '125',
    '150',
    '175',
    '200',
    '225',
    '250',
    '275',
    '300',
    '325',
    '350',
    '375',
    '400',
    '425',
    '450',
    '475',
    '500',
    '550',
    '600',
    '650',
    '700',
    '750',
    '800',
    '850',
    '900',
    '950',
    '1000',
    '1250',
    '1500',
    '1750',
    '2000',
    '2500',
    '3000',
    '3500',
    '4000',
    '4500',
    '5000',
    '7500',
    '8000',
    '8500',
    '9000',
    '9500',
    '10000',
    '12500',
    '15000',
    '17500',
    '20000',
    '25000'
];

type prices = typeof priceOptions[number];

export type PriceFrequency = '' | 'per_week' | 'per_month' | string;
export type PriceMin = prices;
export type PriceMax = prices;

type SelectPriceToRentProps = {
    value: {
        priceMin: PriceMin;
        priceMax: PriceMax;
        priceFrequency: PriceFrequency;
    };
    onChange: ({
        priceMin,
        priceMax,
        priceFrequency
    }: {
        priceMin: PriceMin;
        priceMax: PriceMax;
        priceFrequency: PriceFrequency;
    }) => void;
};

export const SelectPriceToRent = ({
    value,
    onChange
}: SelectPriceToRentProps) => {
    const priceMinOptions =
        value.priceMax !== ''
            ? priceOptions.slice(0, priceOptions.indexOf(value.priceMax) + 1)
            : priceOptions;

    const priceMaxOptions =
        value.priceMin !== ''
            ? priceOptions.slice(priceOptions.indexOf(value.priceMin))
            : priceOptions;

    const priceFrequency = priceFrequencyOptions[value.priceFrequency];

    const frequencyOptions = [
        { id: 'per_month', text: 'Month' },
        { id: 'per_week', text: 'Week' }
    ];

    const minDefaultOption = {
        id: '',
        text: 'No min'
    };

    const maxDefaultOption = {
        id: '',
        text: 'No max'
    };

    const minPriceOptions = priceMinOptions.map((priceMinOption, index) => {
        const price = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
        }).format(Number(priceMinOption));

        return {
            id: priceMinOption,
            text: price
        };
    });

    const maxPriceOptions = priceMaxOptions.map((priceMaxOption, index) => {
        const price = new Intl.NumberFormat('en-GB', {
            style: 'currency',
            currency: 'GBP',
            maximumFractionDigits: 0,
            minimumFractionDigits: 0
        }).format(Number(priceMaxOption));

        return { id: priceMaxOption, text: `${price} ${priceFrequency}` };
    });

    return (
        <div className={filterWrapperStyle}>
            <label
                className={`${filtersLabelStyle} ${fontGroups.ladle}`}
                htmlFor="modal-price-filter"
            >
                {priceRangeLabel}
            </label>
            <div className={priceFrequencyWrapperStyle}>
                <Select
                    onSelectedOptionChange={(o) => {
                        onChange({
                            ...value,
                            priceFrequency: o.id
                        });
                    }}
                    options={frequencyOptions}
                    selectedOption={
                        frequencyOptions.find(
                            (o) => o.id === value.priceFrequency
                        ) ?? frequencyOptions[0]
                    }
                    label={priceFrequencyLabel}
                />
            </div>

            <div className={dropdownWrapperStyle}>
                <div className={leftWrapperStyle}>
                    <Select
                        onSelectedOptionChange={(o) => {
                            onChange({
                                ...value,
                                priceMin: o.id
                            });
                        }}
                        selectedOption={
                            minPriceOptions.find(
                                (o) => o.id === value.priceMin
                            ) ?? minDefaultOption
                        }
                        label={minPriceLabel}
                        options={[minDefaultOption, ...minPriceOptions]}
                    />
                </div>
                <div className={rightWrapperStyle}>
                    <Select
                        options={[maxDefaultOption, ...maxPriceOptions]}
                        onSelectedOptionChange={(o) => {
                            onChange({
                                ...value,
                                priceMax: o.id
                            });
                        }}
                        selectedOption={
                            maxPriceOptions.find(
                                (o) => o.id === value.priceMax
                            ) ?? maxDefaultOption
                        }
                        label={maxPriceLabel}
                    />
                </div>
            </div>
        </div>
    );
};
